"use client";

import { usePostHog } from "posthog-js/react";
import { ZoneLink } from "@/components/layout/zone-link";
import type { Locale } from "@/lib/i18n/config";
import type { Dictionary } from "@/lib/i18n/get-dictionary";

type Props = {
	/** Visible pill text, e.g. "3 baru" / "30+ baru" — see formatNewJobsBadgeLabel. */
	label: string;
	aria: string;
	/** Where the count actually leads — see the note below on why it must. */
	href: string;
	/** Raw count, sent as a capture property so the funnel can be sliced by
	 *  volume (a "1 baru" click is a different intent signal than "30+ baru"). */
	count: number;
};

const MAX_DISPLAYED_COUNT = 30;

/**
 * "{n} baru" for n<=30, "30+ baru" above — PR-3 (2026-08-10, Task 3.2). The
 * bare "99+" pill this replaces tested at 0.39% of pageviews (39 pv / ~18
 * persons over 30d) — a number with no unit next to a wordmark reads as
 * decoration, not a CTA. Labeling the unit is the whole fix; the badge's
 * destination and sr-only sentence are unchanged.
 */
export function formatNewJobsBadgeLabel(
	count: number,
	t: Dictionary,
	locale: Locale,
): string {
	if (count > MAX_DISPLAYED_COUNT) {
		return t.newJobsBadgeMax || (locale === "en" ? "30+ new" : "30+ baru");
	}
	const template =
		t.newJobsBadgeCount || (locale === "en" ? "{count} new" : "{count} baru");
	return template.replace("{count}", String(count));
}

/**
 * The "N new jobs since your last visit" chip.
 *
 * It used to render as a bare `<span>` *inside* the navbar wordmark's own
 * `<Link href={/${locale}}>`. A count chip is the universal "tap me to see the
 * new things" affordance, so users tapped it — and on `/id` or `/en` the
 * wordmark's destination is the page they are already on, so nothing repainted
 * and nothing acknowledged the tap. It was the single most rage-clicked element
 * in the product (26 rage clicks / 30d). The badge is now its own control and
 * goes where the badge promises.
 *
 * It also stops being red. This is good news, not an alarm, and `bg-red-500` +
 * `text-white` measured 3.5:1 — under AA at this size. It now takes DESIGN.md's
 * documented chip treatment: a pill with a low-alpha tint and the accent as the
 * text colour, which is the pre-validated pair. A filled `bg-primary` would
 * read as a primary button, which this is not.
 */
export function BadgeChip({ label, aria, href, count }: Props) {
	const posthog = usePostHog();

	return (
		<ZoneLink
			href={href}
			onClick={() => posthog?.capture("new_jobs_badge_clicked", { count })}
			className="ml-1.5 inline-flex min-h-11 shrink-0 items-center px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
		>
			<span className="sr-only">{aria}</span>
			<span
				aria-hidden="true"
				className="inline-flex min-w-5 items-center justify-center whitespace-nowrap rounded-full bg-primary/10 px-1.5 py-0.5 text-xs font-semibold leading-none text-primary"
			>
				{label}
			</span>
		</ZoneLink>
	);
}
